import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

// Soft drifting light over the cover art, pulsing on the beat.
//
// Driven by the track's BPM and the audio element's clock rather than by a real
// AnalyserNode. Three reasons, in order of how much they mattered:
//
//   1. An AnalyserNode needs the audio to be CORS-readable, and the moment you
//      call createMediaElementSource the element stops routing to the speakers
//      on its own. Both failures are silent - a tainted node returns all zeros,
//      so you get a still animation over working audio, or a lovely animation
//      over no audio at all.
//   2. No FFT per frame, which matters on a page people leave open for hours.
//   3. It looks the same. Nobody is checking the peaks against the waveform.
//
// If real reactivity is ever worth it, the shape to add is an AnalyserNode
// feeding `energy` below; everything else stays.
//
// Deliberately slow. This sits behind lyrics somebody is reading, and a bouncing
// spectrum analyser is exhausting within a minute.

const BLOB_COUNT = 5;

function hexToRgb(hex) {
  const value = String(hex || "").replace("#", "");
  const full = value.length === 3 ? value.split("").map((c) => c + c).join("") : value;
  const int = parseInt(full, 16);
  if (Number.isNaN(int) || full.length !== 6) return [120, 130, 160];
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

export default function Visualizer({ bpm, getPositionMs, palette, paused }) {
  const canvasRef = useRef(null);
  // Read through a ref so a new position callback each render doesn't restart
  // the animation loop.
  const positionRef = useRef(getPositionMs);
  positionRef.current = getPositionMs;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext("2d");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const colors = (palette && palette.length ? palette : ["#2b3a67", "#5c4d7d", "#b34a6e"]).map(hexToRgb);
    const beatMs = 60000 / Math.min(Math.max(bpm || 100, 50), 180);
    let frame = null;

    const resize = () => {
      // Cap the backing store at 2x: beyond that this is pure fill cost for
      // blurred blobs nobody can resolve anyway.
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.clientWidth * ratio;
      canvas.height = canvas.clientHeight * ratio;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const draw = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      const positionMs = positionRef.current() || 0;
      const seconds = positionMs / 1000;

      // A raised cosine over one beat. The previous curve decayed to 0.35 and
      // then jumped back to 1.0 the instant the phase wrapped - a discontinuity
      // at every beat, which reads as a flash rather than a pulse. This one
      // meets itself at the bar line, so there is no edge to see.
      //
      // The range is narrower too (0.35-0.65 rather than 0.35-1.0): the blobs
      // composite with "lighter", so their alpha adds to the cover behind them,
      // and the old swing nearly doubled it. That was survivable while the
      // backdrop was blurred to a colour field and is not now.
      const pulse = 0.5 - 0.5 * Math.cos((positionMs / beatMs) * Math.PI * 2);
      const energy = paused || reduceMotion ? 0.45 : 0.35 + 0.3 * pulse;

      context.clearRect(0, 0, width, height);
      context.globalCompositeOperation = "lighter";

      for (let i = 0; i < BLOB_COUNT; i += 1) {
        const [r, g, b] = colors[i % colors.length];
        // Two incommensurate sine terms per axis, so the blobs never settle
        // into a visible cycle.
        const drift = reduceMotion ? 0 : seconds;
        const x = width * (0.5 + 0.34 * Math.sin(drift * 0.07 + i * 1.9) * Math.cos(drift * 0.031 + i));
        const y = height * (0.5 + 0.34 * Math.cos(drift * 0.053 + i * 2.3));
        const radius = Math.max(width, height) * (0.26 + 0.05 * energy + 0.03 * Math.sin(drift * 0.11 + i));

        const gradient = context.createRadialGradient(x, y, 0, x, y, radius);
        gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.3 * energy + 0.12})`);
        gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
        context.fillStyle = gradient;
        context.beginPath();
        context.arc(x, y, radius, 0, Math.PI * 2);
        context.fill();
      }

      context.globalCompositeOperation = "source-over";
      frame = window.requestAnimationFrame(draw);
    };

    // A tab left open for six hours shouldn't hold a rAF loop it isn't painting.
    const onVisibility = () => {
      if (document.hidden) {
        window.cancelAnimationFrame(frame);
      } else {
        frame = window.requestAnimationFrame(draw);
      }
    };

    resize();
    frame = window.requestAnimationFrame(draw);
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [bpm, palette, paused]);

  return <canvas aria-hidden="true" className="ir-visualizer" ref={canvasRef} />;
}

Visualizer.propTypes = {
  bpm: PropTypes.number,
  getPositionMs: PropTypes.func.isRequired,
  palette: PropTypes.arrayOf(PropTypes.string),
  paused: PropTypes.bool,
};

Visualizer.defaultProps = { bpm: 100, palette: null, paused: false };
